/**
 * Admin — Dev services registry (cost / vendor inventory).
 */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import { ensureNamespaces } from '../../../i18n';
import {
  COST_PRIORITIES,
  DEV_SERVICES_META,
  EXPENSE_JOURNAL,
  QUARTERLY_CHECKLIST,
  SERVICE_SECTIONS,
} from '../../../data/devServicesRegistry';
import {
  AdminEventTypesCard,
  BackScreenDiv,
  ContentDiv,
  DevServicesCostCell,
  DevServicesCostHeaderCell,
  DevServicesMetaList,
  DevServicesSectionTitle,
  DevServicesStatusPill,
  DevServicesTableHeaderDiv,
  Div,
  GRID_TEMPLATE_COLUMNS_COST,
  H1,
  HeaderDiv,
  P,
  ScreenDiv,
  TableColumnDiv,
  TableRowDivDevServices,
  UserColumnRowDiv,
  UserTableBodyDiv,
  UserTableDiv,
  UserTableRowDiv,
} from '../../../styles/styles';

function BillingLinks({ row, t }) {
  if (!row.billingUrl && !row.billingUrl2) {
    return <span>{t('devServices.noLink')}</span>;
  }
  return (
    <span
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        gap: 2,
        alignItems: 'flex-start',
        wordBreak: 'break-word',
      }}
    >
      {row.billingUrl ? (
        <a href={row.billingUrl} target="_blank" rel="noopener noreferrer">
          {row.billingUrlLabel || t('devServices.openLink')}
        </a>
      ) : null}
      {row.billingUrl2 ? (
        <a href={row.billingUrl2} target="_blank" rel="noopener noreferrer">
          {row.billingUrl2Label || t('devServices.openLink')}
        </a>
      ) : null}
    </span>
  );
}


function ServiceTable({ rows, t }) {
  return (
    <UserTableDiv height="auto" justifyStart alignStart style={{ overflow: 'visible' }}>
      <DevServicesTableHeaderDiv>
        <UserColumnRowDiv>{t('devServices.colService')}</UserColumnRowDiv>
        <UserColumnRowDiv>{t('devServices.colPurpose')}</UserColumnRowDiv>
        <UserColumnRowDiv>{t('devServices.colWhere')}</UserColumnRowDiv>
        <UserColumnRowDiv>{t('devServices.colMoney')}</UserColumnRowDiv>
        <UserColumnRowDiv>{t('devServices.colStatus')}</UserColumnRowDiv>
        <UserColumnRowDiv>{t('devServices.colNotes')}</UserColumnRowDiv>
      </DevServicesTableHeaderDiv>
      <UserTableBodyDiv>
        {rows.map((row) => (
          <UserTableRowDiv key={row.id}>
            <TableRowDivDevServices>
              <TableColumnDiv fontSize="13px"><strong>{row.name}</strong></TableColumnDiv>
              <TableColumnDiv>{row.purpose}</TableColumnDiv>
              <TableColumnDiv>{row.where}</TableColumnDiv>
              <TableColumnDiv>{row.money}</TableColumnDiv>
              <TableColumnDiv>
                <DevServicesStatusPill $tone={row.status}>
                  {t(`devServices.status.${row.status}`, row.status)}
                </DevServicesStatusPill>
              </TableColumnDiv>
              <TableColumnDiv>{row.notes || '—'}</TableColumnDiv>
            </TableRowDivDevServices>
          </UserTableRowDiv>
        ))}
      </UserTableBodyDiv>
    </UserTableDiv>
  );
}

function ServiceCards({ rows, t }) {
  return (
    <Div column width="100%" height="auto" gap="0" alignStart justifyStart>
      {rows.map((row) => (
        <AdminEventTypesCard key={row.id}>
          <Div row width="100%" height="auto" justify="space-between" align="center" gap="8px">
            <P style={{ margin: 0, fontWeight: 700 }}>{row.name}</P>
            <DevServicesStatusPill $tone={row.status}>
              {t(`devServices.status.${row.status}`, row.status)}
            </DevServicesStatusPill>
          </Div>
          <P style={{ margin: '6px 0 0', fontSize: 13 }}>{row.purpose}</P>
          <P style={{ margin: '4px 0 0', fontSize: 12, opacity: 0.8 }}>
            {t('devServices.colWhere')}: {row.where}
          </P>
          <P style={{ margin: '4px 0 0', fontSize: 12, opacity: 0.8 }}>
            {t('devServices.colMoney')}: {row.money}
          </P>
          {row.notes ? (
            <P style={{ margin: '6px 0 0', fontSize: 12 }}>{row.notes}</P>
          ) : null}
        </AdminEventTypesCard>
      ))}
    </Div>
  );
}

function CostPriorityCards({ t }) {
  return (
    <Div column width="100%" height="auto" alignStart justifyStart gap="0">
      {COST_PRIORITIES.map((row) => (
        <AdminEventTypesCard key={row.id || `${row.priority}-${row.service}`}>
          <P style={{ margin: 0, fontSize: 11, fontWeight: 600, opacity: 0.7 }}>
            {t(`devServices.priority.${row.priority}`, row.priority)}
            {' · '}
            {row.price}
          </P>
          <P style={{ margin: '4px 0 0', fontWeight: 700 }}>{row.service}</P>
          <P style={{ margin: '6px 0 0', fontSize: 12 }}>{row.notes}</P>
          <P style={{ margin: '6px 0 0', fontSize: 12 }}>
            <BillingLinks row={row} t={t} />
          </P>
        </AdminEventTypesCard>
      ))}
    </Div>
  );
}

function CostPriorityTable({ t }) {
  const grid = GRID_TEMPLATE_COLUMNS_COST;
  return (
    <UserTableDiv height="auto" justifyStart alignStart style={{ width: '100%', overflow: 'visible' }}>
      <DevServicesTableHeaderDiv style={{ gridTemplateColumns: grid }}>
        <DevServicesCostHeaderCell>{t('devServices.colPriority')}</DevServicesCostHeaderCell>
        <DevServicesCostHeaderCell>{t('devServices.colService')}</DevServicesCostHeaderCell>
        <DevServicesCostHeaderCell>{t('devServices.colPrice')}</DevServicesCostHeaderCell>
        <DevServicesCostHeaderCell>{t('devServices.colNotes')}</DevServicesCostHeaderCell>
        <DevServicesCostHeaderCell>{t('devServices.colBillingLink')}</DevServicesCostHeaderCell>
      </DevServicesTableHeaderDiv>
      <UserTableBodyDiv>
        {COST_PRIORITIES.map((row) => (
          <UserTableRowDiv key={row.id || `${row.priority}-${row.service}`}>
            <TableRowDivDevServices style={{ gridTemplateColumns: grid }}>
              <DevServicesCostCell>
                {t(`devServices.priority.${row.priority}`, row.priority)}
              </DevServicesCostCell>
              <DevServicesCostCell fontSize="13px"><strong>{row.service}</strong></DevServicesCostCell>
              <DevServicesCostCell>{row.price}</DevServicesCostCell>
              <DevServicesCostCell>{row.notes}</DevServicesCostCell>
              <DevServicesCostCell>
                <BillingLinks row={row} t={t} />
              </DevServicesCostCell>
            </TableRowDivDevServices>
          </UserTableRowDiv>
        ))}
      </UserTableBodyDiv>
    </UserTableDiv>
  );
}

export default function AdminDevServices() {
  const { t, i18n } = useTranslation('admin');
  const isDesktop = useMediaQuery({ minWidth: '900px' });

  useEffect(() => {
    ensureNamespaces(['admin']);
  }, [i18n.language]);

  return (
    <>
      <BackScreenDiv grayImage />
      <ScreenDiv column justifyContent="flex-start" alignItems="flex-start">
        <HeaderDiv />
        <ContentDiv
          gap={!isDesktop ? '8px' : '12px'}
          paddingBack={isDesktop}
          borderRadius="30px"
          alignStart
          justifyStart
          height="auto"
          heightDesktop="auto"
          heightMobile="auto"
          style={{ overflow: 'visible', width: '100%' }}
        >
          {isDesktop && <H1>{t('devServices.title')}</H1>}
          <P style={{ marginTop: 0, maxWidth: 720 }}>{t('devServices.subtitle')}</P>

          <DevServicesMetaList height="auto" justifyStart alignStart>
            <div>
              <strong>{t('devServices.metaProject')}:</strong>
              {' '}
              {DEV_SERVICES_META.firebaseProject}
            </div>
            <div>
              <strong>{t('devServices.metaDomain')}:</strong>
              {' '}
              {DEV_SERVICES_META.domain}
            </div>
            <div>
              <strong>{t('devServices.metaUpdated')}:</strong>
              {' '}
              {DEV_SERVICES_META.updatedAt}
            </div>
            <div>
              <strong>{t('devServices.metaDoc')}:</strong>
              {' '}
              {DEV_SERVICES_META.docPath}
            </div>
          </DevServicesMetaList>

          <DevServicesSectionTitle>{t('devServices.costTitle')}</DevServicesSectionTitle>
          {isDesktop ? <CostPriorityTable t={t} /> : <CostPriorityCards t={t} />}

          <DevServicesSectionTitle>{t('devServices.registryTitle')}</DevServicesSectionTitle>
          {SERVICE_SECTIONS.map((section) => (
            <Div
              key={section.id}
              column
              width="100%"
              height="auto"
              alignStart
              justifyStart
              style={{ marginBottom: 8 }}
            >
              <P style={{ margin: '12px 0 6px', fontWeight: 700, width: '100%', textAlign: 'left' }}>
                {t(`devServices.${section.titleKey}`)}
              </P>
              {isDesktop ? (
                <ServiceTable rows={section.rows} t={t} />
              ) : (
                <ServiceCards rows={section.rows} t={t} />
              )}
            </Div>
          ))}

          <DevServicesSectionTitle>{t('devServices.journalTitle')}</DevServicesSectionTitle>
          {EXPENSE_JOURNAL.length === 0 ? (
            <P>{t('devServices.journalEmpty')}</P>
          ) : isDesktop ? (
            <UserTableDiv height="auto" justifyStart alignStart>
              <DevServicesTableHeaderDiv
                style={{
                  gridTemplateColumns:
                    'minmax(110px, 0.9fr) minmax(140px, 1.2fr) 90px 80px minmax(160px, 2fr)',
                }}
              >
                <UserColumnRowDiv>{t('devServices.colPeriod')}</UserColumnRowDiv>
                <UserColumnRowDiv>{t('devServices.colService')}</UserColumnRowDiv>
                <UserColumnRowDiv>{t('devServices.colAmount')}</UserColumnRowDiv>
                <UserColumnRowDiv>{t('devServices.colCurrency')}</UserColumnRowDiv>
                <UserColumnRowDiv>{t('devServices.colComment')}</UserColumnRowDiv>
              </DevServicesTableHeaderDiv>
              <UserTableBodyDiv>
                {EXPENSE_JOURNAL.map((row) => (
                  <UserTableRowDiv key={row.id}>
                    <TableRowDivDevServices
                      style={{
                        gridTemplateColumns:
                          'minmax(110px, 0.9fr) minmax(140px, 1.2fr) 90px 80px minmax(160px, 2fr)',
                      }}
                    >
                      <TableColumnDiv>{row.period}</TableColumnDiv>
                      <TableColumnDiv fontSize="13px"><strong>{row.service}</strong></TableColumnDiv>
                      <TableColumnDiv>{row.amount}</TableColumnDiv>
                      <TableColumnDiv>{row.currency}</TableColumnDiv>
                      <TableColumnDiv>{row.comment || '—'}</TableColumnDiv>
                    </TableRowDivDevServices>
                  </UserTableRowDiv>
                ))}
              </UserTableBodyDiv>
            </UserTableDiv>
          ) : (
            <Div column width="100%" height="auto" alignStart justifyStart>
              {EXPENSE_JOURNAL.map((row) => (
                <AdminEventTypesCard key={row.id}>
                  <P style={{ margin: 0, fontWeight: 700 }}>{row.service}</P>
                  <P style={{ margin: '4px 0 0', fontSize: 13 }}>
                    {row.period}
                    {' · '}
                    {row.amount}
                    {' '}
                    {row.currency}
                  </P>
                  {row.comment ? (
                    <P style={{ margin: '4px 0 0', fontSize: 12 }}>{row.comment}</P>
                  ) : null}
                </AdminEventTypesCard>
              ))}
            </Div>
          )}

          <DevServicesSectionTitle>{t('devServices.checklistTitle')}</DevServicesSectionTitle>
          <ul style={{ margin: '0 0 24px', paddingLeft: 18, textAlign: 'left', width: '100%' }}>
            {QUARTERLY_CHECKLIST.map((item) => (
              <li key={item} style={{ marginBottom: 6, fontSize: 13 }}>{item}</li>
            ))}
          </ul>
        </ContentDiv>
      </ScreenDiv>
    </>
  );
}
